import React, { useState } from 'react';
import axios from 'axios';
import { baseUrl, endpoints } from '../../../api/api';
import { AdminBook } from '../types';

interface IsbnSectionProps {
  book: AdminBook;
  onUpdated: () => void;
}

type Step = 'idle' | 'saving' | 'regenerating' | 'done' | 'error';

const IsbnSection: React.FC<IsbnSectionProps> = ({ book, onUpdated }) => {
  const [isbnInput, setIsbnInput]   = useState(book.isbn || '');
  const [step, setStep]             = useState<Step>('idle');
  const [message, setMessage]       = useState('');
  const [isEditing, setIsEditing]   = useState(!book.isbn);

  const isEditorBook  = !!(book.content as any)?.html?.trim();
  const isUploadBook  = !!(book.uploadedFile as any)?.fileId;
  const token         = () => localStorage.getItem('accessToken');

  const handleSaveISBN = async () => {
    const clean = isbnInput.replace(/[-\s]/g, '');
    if (!/^(\d{10}|\d{13})$/.test(clean)) {
      setMessage('ISBN must be 10 or 13 digits (hyphens allowed).');
      setStep('error');
      return;
    }

    setStep('saving');
    setMessage('');
    try {
      await axios.put(
        `${baseUrl}${endpoints.adminUpdateISBN.replace(':id', book._id)}`,
        { isbn: clean },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setIsEditing(false);
      setStep('idle');
      setMessage('ISBN saved.');
      onUpdated();
    } catch (err: any) {
      setStep('error');
      setMessage(err.response?.data?.message || 'Failed to save ISBN.');
    }
  };

  const handleRegeneratePDF = async () => {
    setStep('regenerating');
    setMessage('');
    try {
      await axios.post(
        `${baseUrl}${endpoints.adminRegeneratePDF.replace(':id', book._id)}`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setStep('done');
      setMessage('PDF regenerated with ISBN. Author can now download the final copy.');
      onUpdated();
    } catch (err: any) {
      setStep('error');
      setMessage(err.response?.data?.message || 'PDF regeneration failed.');
    }
  };

  const isbnDisplay = book.isbn
    ? book.isbn.replace(/(\d{3})(\d{1})(\d{5})(\d{3})(\d{1})/, '$1-$2-$3-$4-$5')
    : null;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">ISBN</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {isEditorBook
              ? 'Saving ISBN will update the copyright page on the next PDF generation.'
              : 'Uploaded-file book — ISBN is saved to records only. Author must re-upload a file with the ISBN embedded.'}
          </p>
        </div>
        {book.isbn && !isEditing && (
          <button
            onClick={() => { setIsEditing(true); setMessage(''); setStep('idle'); }}
            className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 px-2.5 py-1 rounded-md"
          >
            Edit
          </button>
        )}
      </div>

      <div className="px-6 py-4 space-y-4">
        {/* Current ISBN status */}
        {!isEditing && book.isbn && (
          <div className="flex items-center gap-3">
            <span className="text-sm font-mono text-gray-900 tracking-wider">{isbnDisplay || book.isbn}</span>
            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium">Assigned</span>
          </div>
        )}

        {!isEditing && !book.isbn && (
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">No ISBN assigned</span>
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs text-blue-600 hover:underline"
            >
              Assign one
            </button>
          </div>
        )}

        {/* Input form */}
        {isEditing && (
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={isbnInput}
              onChange={e => { setIsbnInput(e.target.value); setStep('idle'); setMessage(''); }}
              placeholder="e.g. 978-93-XXXXX-XX-X"
              className="px-3 py-2 text-sm border border-gray-300 rounded-md w-60 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={step === 'saving'}
            />
            <button
              onClick={handleSaveISBN}
              disabled={step === 'saving' || !isbnInput.trim()}
              className="px-4 py-2 text-sm bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {step === 'saving' && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {step === 'saving' ? 'Saving…' : 'Save ISBN'}
            </button>
            {book.isbn && (
              <button
                onClick={() => { setIsEditing(false); setIsbnInput(book.isbn || ''); setStep('idle'); setMessage(''); }}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Cancel
              </button>
            )}
          </div>
        )}

        {/* Regenerate PDF — only for editor books, only once ISBN is saved */}
        {book.isbn && isEditorBook && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Regenerate Final PDF</p>
                <p className="text-xs text-gray-400 mt-0.5">Rebuilds the PDF with the current ISBN on the copyright page.</p>
              </div>
              <button
                onClick={handleRegeneratePDF}
                disabled={step === 'regenerating' || step === 'saving'}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {step === 'regenerating' && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {step === 'regenerating' ? 'Generating…' : 'Regenerate PDF'}
              </button>
            </div>
            {step === 'done' && (
              <p className="mt-2 text-xs text-green-600 font-medium">PDF updated — author can download the final copy.</p>
            )}
          </div>
        )}

        {book.isbn && isUploadBook && !isEditorBook && (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              This book uses an uploaded file. PDF regeneration is not available — the author needs to re-upload a file with the ISBN embedded.
            </p>
          </div>
        )}

        {/* Status messages */}
        {message && (
          <p className={`text-xs font-medium ${step === 'error' ? 'text-red-600' : step === 'done' ? 'text-green-600' : 'text-gray-600'}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default IsbnSection;
